<script setup lang="ts">
import {
  type DetailGoodsItemWrapperConfig,
  type GoodsItem,
  type GoodsItemConfig,
  IMAGE_VIEW_TYPE,
  PRICE_TYPE
} from '~/components/common/modules/types/goods-item-type'
import {
  type ChannelId,
  type OnAirTextType,
  GOODS_DISP_TYPE_CD,
  CHANNEL_ID,
  COUNSEL_ACCEPTANCE_GOODS_TEXT
} from '~/constants/common/goods/goods'
import { useDisplayInfo } from '~/composable/common/goods/use-display-info'

defineOptions({
  name: 'GoodsItemWrapper'
})

const props = defineProps({
  config: Object as PropType<DetailGoodsItemWrapperConfig>
})

const propsConfig = computed(() => props.config)

// UI_타입
const viewType = computed(() => {
  return propsConfig.value?.viewType
})

// TV상품 유무: tv상품이 아니면 온라인 상품
const isGoodsTv = computed(() => {
  const channelId = propsConfig.value?.busChnId as ChannelId
  if (channelId === CHANNEL_ID.TV || channelId === CHANNEL_ID.SHOPPLUS) {
    return true
  } else {
    return false
  }
})

// 방송한정 구매가능 여부
const isBroadcastAvailableBuy = computed(() => {
  if (propsConfig.value?.stockOutFlag === 'B') {
    return true
  } else {
    return false
  }
})

// 전시 타입 코드 > 테스트용
const dispTypeCd = computed(() => {
  return GOODS_DISP_TYPE_CD.GENERAL_NO_SINGLE
})

/**
 * 전시상품정보
 */
const { displayInfo, getOnAirText } = useDisplayInfo(dispTypeCd.value)

// 성인상품_유무
const isAdultGoods = computed(() => {
  return propsConfig.value?.adltGoodsYn === 'Y'
})

// 이미지
const image = computed(() => {
  if (propsConfig.value?.imageViewType === IMAGE_VIEW_TYPE.URL) {
    return {
      type: IMAGE_VIEW_TYPE.URL,
      goodsCd: propsConfig.value?.goodsCd,
      imageUrl: propsConfig.value?.imageUrl
    }
  } else {
    return {
      type: IMAGE_VIEW_TYPE.CODE,
      goodsCd: propsConfig.value?.goodsCd,
      imageUrl: undefined
    }
  }
})

// 찜
const wish = computed(() => {
  return propsConfig.value?.wishYn
})

// 방송중_텍스트 : '방송중 상담가능' | '방송중 구매가능'
const onAirText = computed<OnAirTextType>(() => {
  return getOnAirText()
})

// 상품명
const productNm = computed(() => {
  return propsConfig.value?.productNm
})

// 가격영역
const priceArea = computed(() => {
  const type = displayInfo.value.isCounseling ? PRICE_TYPE.TEXT : PRICE_TYPE.PRICE
  return {
    type,
    dcRate: propsConfig.value?.dcRate,
    dcPrice: propsConfig.value?.dcPrice,
    salePrice: propsConfig.value?.salePrice,
    goodsText: COUNSEL_ACCEPTANCE_GOODS_TEXT
  }
})

// 이미지타입_URL_유무
const isUrlType = computed(() => {
  return image.value.type === IMAGE_VIEW_TYPE.URL
})

// 찜 노출여부
const isShowWish = computed(() => {
  return propsConfig.value?.isShowWish
})

const itemInfo = computed<GoodsItem>(() => {
  return {
    image: image.value,
    wishYn: wish.value,
    onAirText: onAirText.value,
    productNm: productNm.value,
    priceArea: priceArea.value
  }
})
const configInfo = ref<GoodsItemConfig>({
  viewType: viewType.value,
  isGoodsTv: isGoodsTv.value,
  isBroadcastAvailableBuy: isBroadcastAvailableBuy.value,
  displayInfo: displayInfo.value,
  isAdultGoods: isAdultGoods.value,
  isUrlType: isUrlType.value,
  isShowWish: isShowWish.value
})
</script>

<template>
  <div>
    <slot :itemInfo="itemInfo" :configInfo="configInfo"></slot>
  </div>
</template>

<style scoped></style>
